import {
  Box,
  Flex,
  HStack,
  VStack,
  Divider,
  useBreakpointValue,
  Skeleton,
  Tooltip,
} from '@chakra-ui/react';
import React from 'react';

import {
  utils as commonUtils,
  date as commonDate,
} from '@finley-technologies/common';

import {
  AccruedInterestAndFeesSummaryGQLType,
  ResolvedLoanFragmentGQLType,
} from 'types/dashboard-core-gqlTypes.generated';

import { LoanPricingOption } from './LoanPricingOption';
import { colors } from 'packages/common-frontend/colors';
import { Typography } from 'packages/common-frontend/components/Typography';
import { formatCurrencyAmount } from 'packages/common-frontend/utils';
import { Bank } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';

const YtdTooltipContent = ({
  data,
  title,
}: {
  data: AccruedInterestAndFeesSummaryGQLType;
  title: string;
}) => {
  return (
    <VStack
      sx={{
        align: 'start',
        bgColor: 'black',
        border: '1px solid',
        borderRadius: 'md',
        minW: '25rem',
        p: '1rem',
        w: '100%',
      }}
    >
      <HStack key="total" justify="space-between" w="100%">
        <Typography TextProps={{ color: 'white' }}>Total</Typography>
        <Typography TextProps={{ color: 'white' }}>
          {typeof data.totalAmount === 'number'
            ? formatCurrencyAmount(data.totalAmount, Bank.Currency.USD)
            : '-'}
        </Typography>
      </HStack>

      {data.annualAmounts.length > 0 &&
        data.annualAmounts.map((annualAmount, idx) => (
          <HStack key={annualAmount?.year} justify="space-between" w="100%">
            <Typography TextProps={{ color: 'white' }}>
              {idx === 0 ? `YTD ${title}` : `${annualAmount?.year} ${title}`}
            </Typography>
            <Typography TextProps={{ color: 'white' }}>
              {typeof annualAmount?.amount === 'number'
                ? formatCurrencyAmount(annualAmount.amount, Bank.Currency.USD)
                : '-'}
            </Typography>
          </HStack>
        ))}
    </VStack>
  );
};

const InterestAndFeesSection = ({
  loan,
}: {
  loan: ResolvedLoanFragmentGQLType | undefined;
}) => {
  return (
    <VStack
      sx={{
        border: '1px solid',
        borderColor: 'gray.200',
        borderRadius: 'md',
        flex: 1,
        gap: '.5rem',
        minW: '15rem',
        p: '1rem',
      }}
    >
      <Tooltip
        label={
          typeof loan?.fees.totalAccruedInterestAndFees.totalAmount ===
            'number' && (
            <YtdTooltipContent
              data={loan.fees.totalAccruedInterestAndFees}
              title="Total"
            />
          )
        }
        placement="top-start"
        sx={{
          borderRadius: 'md',
          p: '0',
        }}
      >
        <HStack justify="space-between" w="100%">
          <Typography fontWeight="medium">YTD Total</Typography>
          <Typography fontWeight="medium">
            {loan?.fees.totalAccruedInterestAndFees.annualAmounts[0]
              ? formatCurrencyAmount(
                  loan.fees.totalAccruedInterestAndFees.annualAmounts[0].amount,
                  Bank.Currency.USD,
                )
              : '-'}
          </Typography>
        </HStack>
      </Tooltip>
      <Divider />
      <Tooltip
        label={
          loan?.fees.accruedInterest && (
            <YtdTooltipContent
              data={loan.fees.accruedInterest}
              title="Interest"
            />
          )
        }
        placement="bottom-start"
        sx={{
          borderRadius: 'md',
          p: '0',
        }}
      >
        <HStack justify="space-between" w="100%">
          <Flex maxW="50%">
            <Typography variant="base-muted">YTD Interest</Typography>
          </Flex>
          <Flex textAlign="right">
            <Typography variant="base-muted">
              {loan?.fees.accruedInterest.annualAmounts[0]
                ? formatCurrencyAmount(
                    loan?.fees.accruedInterest.annualAmounts[0].amount,
                    Bank.Currency.USD,
                  )
                : '-'}
            </Typography>
          </Flex>
        </HStack>
      </Tooltip>
      <Tooltip
        label={
          loan?.fees.accruedServiceFees && (
            <YtdTooltipContent
              data={loan.fees.accruedServiceFees}
              title="Service Fees"
            />
          )
        }
        placement="bottom-start"
        sx={{
          borderRadius: 'md',
          p: '0',
        }}
      >
        <HStack justify="space-between" w="100%">
          <Flex maxW="50%">
            <Typography variant="base-muted">YTD Service Fees</Typography>
          </Flex>
          <Flex textAlign="right">
            <Typography variant="base-muted">
              {loan?.fees.accruedServiceFees.annualAmounts[0]
                ? formatCurrencyAmount(
                    loan?.fees.accruedServiceFees.annualAmounts[0].amount,
                    Bank.Currency.USD,
                  )
                : '-'}
            </Typography>
          </Flex>
        </HStack>
      </Tooltip>
    </VStack>
  );
};

export const LoanDetails = ({
  loan,
  loading,
  showDates = true,
  showLoanNamingInfo = false,
}: {
  loan: ResolvedLoanFragmentGQLType | undefined;
  loading: boolean;
  showDates?: boolean;
  showLoanNamingInfo?: boolean;
}) => {
  const memoTextAlign = useBreakpointValue(
    { base: 'left', md: 'right' },
    { ssr: false },
  );
  const memoJustifyContent = useBreakpointValue(
    { base: 'flex-start', md: 'flex-end' },
    { ssr: false },
  );

  return (
    <VStack direction="column" gap="1rem" align="start" w="100%">
      <Typography variant="section-title" trim>
        Loan Details
      </Typography>
      <VStack
        sx={{
          w: '100%',
          borderWidth: '1px',
          borderRadius: 'md',
          p: '2rem',
          gap: '1rem',
        }}
      >
        {showLoanNamingInfo && (
          <>
            {loan?.name && (
              <HStack
                sx={{
                  justifyContent: 'space-between',
                  wrap: 'wrap',
                  w: '100%',
                }}
              >
                <Typography fontWeight="medium">Loan Name</Typography>
                {loading ? (
                  <Skeleton w="10rem" h="1rem" />
                ) : (
                  <Typography variant="base-muted">
                    {loan?.name ? loan.name : '-'}
                  </Typography>
                )}
              </HStack>
            )}
            <HStack
              sx={{
                justifyContent: 'space-between',
                wrap: 'wrap',
                w: '100%',
              }}
            >
              <Typography fontWeight="medium">Loan ID</Typography>
              {loading ? (
                <Skeleton w="10rem" h="1rem" />
              ) : (
                <Flex flexWrap="wrap" maxW="55%" textAlign="end">
                  <Typography variant="base-muted">
                    {loan?.id}
                    {loan?.externalLoanId && ` (${loan.externalLoanId})`}
                  </Typography>
                </Flex>
              )}
            </HStack>
          </>
        )}
        <HStack
          sx={{
            justifyContent: 'space-between',
            wrap: 'wrap',
            w: '100%',
          }}
        >
          <Typography fontWeight="medium">
            Global Outstanding Balance
          </Typography>
          {loading ? (
            <Skeleton w="10rem" h="1rem" />
          ) : (
            <Typography fontWeight="medium">
              {typeof loan?.funding.totalOutstandingBalance === 'number'
                ? formatCurrencyAmount(
                    loan.funding.totalOutstandingBalance,
                    Bank.Currency.USD,
                  )
                : '-'}
            </Typography>
          )}
        </HStack>

        <LoanPricingOption loan={loan} loading={loading} />
        <HStack
          sx={{
            fontWeight: 500,
            gap: '1rem',
            justifyContent: 'space-between',
            w: '100%',
            wrap: 'wrap',
          }}
        >
          <Typography fontWeight="medium">Interest Rate Schedule</Typography>
          {loading ? (
            <Skeleton w="10rem" h="1rem" />
          ) : (
            <Typography fontWeight="medium">
              {loan
                ? `${loan.pricing.accrual.basis}, ${commonUtils.toTitleCase(
                    loan.pricing.accrual.cadence.type,
                  )} Accrual`
                : '-'}
            </Typography>
          )}
        </HStack>

        <HStack
          alignItems="flex-start"
          sx={{
            gap: '1rem',
            justifyContent: 'space-between',
            py: '.25rem',
            w: '100%',
            wrap: 'wrap',
          }}
        >
          <Flex fontWeight={500} w="20rem">
            Interest and Fees
          </Flex>
          {loading ? (
            <Skeleton w="10rem" h="1rem" />
          ) : (
            <InterestAndFeesSection loan={loan} />
          )}
        </HStack>
        {showDates && (
          <>
            <HStack
              sx={{
                justifyContent: 'space-between',
                w: '100%',
                wrap: 'wrap',
              }}
            >
              <Typography fontWeight="medium">Funding Date</Typography>
              {loading ? (
                <Skeleton w="10rem" h="1rem" />
              ) : (
                <Typography variant="base-muted">
                  {typeof loan?.dates.originationDate === 'string'
                    ? commonDate.fullDateStrToHumanDateString(
                        loan.dates.originationDate,
                      )
                    : '-'}
                </Typography>
              )}
            </HStack>
            <HStack
              sx={{
                justifyContent: 'space-between',
                wrap: 'wrap',
                w: '100%',
              }}
            >
              <Typography fontWeight="medium">Maturity Date</Typography>
              {loading ? (
                <Skeleton w="10rem" h="1rem" />
              ) : (
                <Typography variant="base-muted">
                  {typeof loan?.dates.maturityDate === 'string'
                    ? commonDate.fullDateStrToHumanDateString(
                        loan.dates.maturityDate,
                      )
                    : '-'}
                </Typography>
              )}
            </HStack>
          </>
        )}
        <HStack
          sx={{
            justifyContent: 'space-between',
            gap: '1rem',
            wrap: 'wrap',
            alignItems: 'start',
            w: '100%',
          }}
        >
          <Box minW="20rem" fontWeight={500}>
            Memo
          </Box>
          {loading ? (
            <Skeleton w="10rem" h="1rem" />
          ) : (
            <Flex
              sx={{
                flex: 1,
                gap: '.5rem',
                color: colors.gray[600],
                justifyContent: memoJustifyContent,
                textAlign: memoTextAlign,
                minW: '10rem',
              }}
            >
              {loan?.memo ?? '-'}
            </Flex>
          )}
        </HStack>
      </VStack>
    </VStack>
  );
};
